/**
 * Shared command-room websocket helpers.
 *
 * `ConnStatus` is the **unified** connection-lifecycle type for the optimistic
 * command rooms (roster + encounter). The presence and game-login sockets are
 * deliberately NOT folded in — they carry genuinely different lifecycles: presence is
 * `open`/`closed`, game adds auth states like `authenticated`/`rejected`/`kicked`.
 *
 * `roomWsUrl(path)` resolves a room ws URL from the browser location, parameterized on
 * the room's `WS_*_PATH`.
 *
 * `startKeepalive(ws)` / `reconnectDelayMs(attempt)` are the two liveness primitives
 * EVERY room socket needs — see the block comment on `startKeepalive` for why an idle
 * socket is not a safe steady state.
 */
import { encode } from "@lancer-vtt/shared";

/**
 * The live ws connection status the command-room pages surface: `connecting`
 * (socket opening / awaiting the welcome handshake) → `connected` (welcomed,
 * authoritative state seeded) → `disconnected` (socket closed/errored).
 */
export type ConnStatus = "connecting" | "connected" | "disconnected";

/**
 * The refusal every write gets while its socket is not live — a room's `dispatch`/`undo`/intent
 * send, or a session writer. Writes during an outage are refused, never queued: a queued attack
 * would roll against whatever the board is when it finally lands (`connection-grace`). The app-wide
 * link pill says why; this note says the one action did not happen.
 */
export const LINK_DOWN_NOTE = "Link down — not sent";

/** Resolve a room ws URL from the browser location. */
export function roomWsUrl(path: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  return `${proto}://${location.host}${path}`;
}

/**
 * How often a room socket sends its liveness `ping`. Comfortably under the ~60s
 * idle cutoff measured on the live deployment (see {@link startKeepalive}), and
 * cheap: a 2-field frame plus the server's `pong`, per socket, per 25s.
 */
export const KEEPALIVE_MS = 25_000;

/**
 * How long a socket may go without ANY inbound frame before it is declared dead. The server
 * answers every keepalive `ping` with a `pong`, so a live socket never goes quieter than
 * {@link KEEPALIVE_MS}; the 10s on top is the pong's round trip with slack.
 */
export const DEAD_PATH_MS = KEEPALIVE_MS + 10_000;

/** How often the dead-path watch looks at the inbound clock. */
const DEAD_PATH_CHECK_MS = 5_000;

export type KeepaliveOptions = {
  /** The ping interval; {@link KEEPALIVE_MS} by default. */
  intervalMs?: number;
  /** The inbound silence that declares the path dead; {@link DEAD_PATH_MS} by default. */
  deadAfterMs?: number;
  /**
   * Called once when the path is declared dead (inbound silence, or the browser reporting
   * `offline`), right after the keepalive closes the socket. The owner runs its drop path here
   * instead of waiting for the close event, which a dead path may not deliver for minutes.
   */
  onDead?: () => void;
};

/**
 * Keep a room socket warm by sending `ping` every {@link KEEPALIVE_MS} while it's open.
 * Returns the stop function (idempotent) — call it from the socket's own `close`
 * handler AND from the owner's teardown.
 *
 * **Why this is not optional.** An *idle* websocket is not a stable steady state on a
 * real internet path: a NAT/firewall on the route silently evicts the flow and the
 * client only learns about it as an abrupt 1006 close. Measured against the live
 * deployment, an idle `/ws/game` socket died at **60.4s**, three runs out of three,
 * while a socket sending a frame every 20s survived indefinitely. It is neither the
 * Bun origin (an idle socket straight to `:3001` lived past 210s) nor Caddy (an idle
 * socket through TLS from the box itself lived past 200s) — it is the path in between,
 * which we do not control and which differs for every player. So the fix has to be
 * traffic we generate ourselves.
 *
 * The `ping`/`pong` pair is already in the protocol (`PingMessage`, `shared/src/index.ts`)
 * and every room answers it (`gameRoom`/`rosterRoom`/`encounterRoom`/`presenceRoom`'s
 * `case "ping"`), so this needs no wire change. The server's `pong` matters as much as
 * the ping: it warms the *return* path, which a client-only frame would leave idle.
 *
 * Keepalive is the *prevention* half. It cannot cover a genuine drop (sleep, a wifi
 * switch, a server restart), so every socket owner still needs the *recovery* half —
 * reconnect on unexpected close, paced by {@link reconnectDelayMs}.
 *
 * **It also notices a dead path without waiting for TCP** (`connection-grace` 3.1). With wifi
 * off, an established socket usually stays OPEN: nothing arrives, nothing errors, and the close
 * event can take minutes. So the keepalive records the last inbound frame and declares the path
 * dead after {@link DEAD_PATH_MS} of silence, or at once on the browser's `offline` event: it
 * closes the socket and calls `onDead`, and the owner's ordinary drop path takes over (the link
 * pill, the write gate, the reconnect).
 */
export function startKeepalive(ws: WebSocket, opts: KeepaliveOptions = {}): () => void {
  const { intervalMs = KEEPALIVE_MS, deadAfterMs = DEAD_PATH_MS, onDead } = opts;
  let lastInbound = Date.now();
  const onMessage = (): void => {
    lastInbound = Date.now();
  };
  let stopped = false;
  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    clearInterval(pingTimer);
    clearInterval(watchTimer);
    ws.removeEventListener("message", onMessage);
    if (typeof window !== "undefined") window.removeEventListener("offline", declareDead);
  };
  // The dead-path half (see the doc above): stop, close, and tell the owner now, because the
  // close event of a socket whose path is gone can take the browser minutes to deliver.
  function declareDead(): void {
    if (stopped) return;
    stop();
    ws.close();
    onDead?.();
  }
  const pingTimer = setInterval(() => {
    // Only OPEN sockets: sending on CONNECTING throws, and on CLOSING/CLOSED it's a no-op
    // we'd rather not attempt at all.
    if (ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(encode({ type: "ping", t: Date.now() }));
    } catch {
      /* the socket died between the readyState check and the send — the close handler owns it */
    }
  }, intervalMs);
  const watchTimer = setInterval(() => {
    if (ws.readyState === WebSocket.OPEN && Date.now() - lastInbound > deadAfterMs) declareDead();
  }, DEAD_PATH_CHECK_MS);
  ws.addEventListener("message", onMessage);
  if (typeof window !== "undefined") window.addEventListener("offline", declareDead);
  return stop;
}

/**
 * The reconnect **backoff cap**, in attempts: {@link reconnectDelayMs} is clamped to this
 * exponent (past it every retry is the same 15s), and a socket owner treats reaching it as
 * the moment to escalate the UI from "blip" to "link lost". It is NOT a give-up count —
 * both the room sockets and `/ws/game` retry indefinitely.
 */
export const RECONNECT_MAX_ATTEMPTS = 6;

declare global {
  /** TEST-ONLY — a multiplier on every {@link reconnectDelayMs} result, read on each call.
   *  Unset (production) means 1. `reconnect-never-gives-up.e2e.ts` sets it so the real timers
   *  and the real attempt-counted escalation run on a compressed clock, instead of spending
   *  minutes of wall time on the ~45s fast budget and the 15s cap. */
  // eslint-disable-next-line no-var
  var __reconnectTimeScale: number | undefined;
}

/** The {@link __reconnectTimeScale} test knob, or 1 when unset or not a positive number. */
function reconnectTimeScale(): number {
  const scale = globalThis.__reconnectTimeScale;
  return typeof scale === "number" && scale > 0 ? scale : 1;
}

/**
 * Backoff for reconnect attempt `attempt` (0-based): ~1s, 2s, 4s, 8s, 15s, 15s…, with
 * ±20% jitter so a server restart doesn't bring every client back in lockstep. Capped
 * at 15s — this is a live game table, not a background sync; a player should be back in
 * well under a turn.
 */
export function reconnectDelayMs(attempt: number): number {
  const base = Math.min(1000 * 2 ** attempt, 15_000);
  return Math.round(base * (0.8 + Math.random() * 0.4) * reconnectTimeScale());
}
